import { FC, useMemo } from 'react';

// Third-party
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import Icon from '@mdi/react';
import moment from 'moment-timezone';
import { mdiChevronLeft, mdiChevronRight } from '@mdi/js';
import { useTheme } from '@mui/material';

// Components
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from 'react-i18next';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { BaseTooltip } from '@mgm/common/components/base';
import { getTimezone } from '@mgm/common/utils';
import { BaseFilterMenu } from '../../../../components/base';
import { ToolbarWrapper } from '../../Shared/components';

// Data
import { CUSTOM_ACTIONS, getViewOptions } from '../../../../data';

// Types
import { TCalendarToolbar } from '../type';

// Hooks
import { usePrint } from '../../../../hooks/usePrint';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';

// Redux-toolkit
import { getIsPrintMode } from '../../../../store/common/selectors';
import appLogo from '../../../../assets/appLogo.png';
import { getIsDisplayPublishBtn } from '../../../../store/schedules/selectors';
import { useEditScheduleShiftAPI } from '../../../../hooks/api/useSchedules';
import { TMember, TScheduleShift } from '../../../../types';
import { CalendarToolbarIcon } from './CalendarToolbarIcon';
import { useFilter } from '../../../../hooks/useFilter';
import { useCalendarFilter } from '../hooks';
import { schedulesActions } from '../../../../store/schedules/slice';
import { commonActions } from '../../../../store/common/slice';
import { useSchedulePermissions } from '../../Shared/hooks';

const CalendarToolbar: FC<TCalendarToolbar> = ({
  date,
  view,
  onNextDate,
  onPrevDate,
  onToday,
  onChangeView,
  printRef,
  updatedSchedules,
  ...other
}) => {
  const { calendarFilterItems } = useCalendarFilter();
  const theme = useTheme() as TCustomTheme;
  const { t } = useTranslation('schedule');
  const { isLight, isMobile } = useCustomTheme(theme);
  const { handlePrint } = usePrint(printRef);
  const isPrintMode = useAppSelector(getIsPrintMode);
  const isDisplayPublishBth = useAppSelector(getIsDisplayPublishBtn);
  const { mutateAsync, isLoading } = useEditScheduleShiftAPI();
  const { filterItems } = useFilter(calendarFilterItems, false);
  const dispatch = useAppDispatch();

  const { schedulePermissions } = useSchedulePermissions();

  const month = useMemo(() => moment.tz(date, getTimezone()).format('MMMM').toLowerCase(), [date]);

  const mapValueToActions: { [key: string]: () => void } = {
    print: handlePrint,
    dayOff: () => {
      dispatch(commonActions.setIsOpenPagesNav(false));
      dispatch(schedulesActions.setIsOpenDayOffNav(true));
    },
    addShift: () => {
      dispatch(schedulesActions.setSelectedScheduleShift({} as TScheduleShift));
      dispatch(schedulesActions.setSelectedShiftMember({} as TMember));
      dispatch(schedulesActions.setSelectedShiftDate(moment().format()));
      dispatch(schedulesActions.setIsOpenDayOffNav(false));
      dispatch(commonActions.setIsOpenPagesNav(true));
    },
  };

  const handleToggle = (value: string) => {
    if (CUSTOM_ACTIONS.includes(value)) {
      mapValueToActions[value]();
    } else if (value !== 'filter') onChangeView(value);
  };

  const publishSchedules = async () => {
    const scheduleShifts = Object.values(updatedSchedules).reduce((acc: Array<TScheduleShift>, item) => {
      if (item.schedules?.length) {
        acc.push(...item.schedules);
      }
      return acc;
    }, []);

    const data = {
      schedulesData: scheduleShifts,
    };

    await mutateAsync(data);
  };

  return (
    <ToolbarWrapper {...other}>
      {!isPrintMode && (
        <Stack direction="row" spacing={1.5}>
          {getViewOptions(isMobile, schedulePermissions.isAllowManage as boolean).map((viewOption) => (
            <BaseTooltip key={viewOption.value} tooltipText={t(`${viewOption.label}`)}>
              <ToggleButton
                value={view}
                component="div"
                selected={viewOption.value === view}
                onChange={() => handleToggle(viewOption.value)}
                sx={{
                  width: viewOption.buttonText ? 'auto' : 32,
                  height: 32,
                  padding: viewOption.buttonText ? 1 : 0,
                }}
              >
                {viewOption.value !== 'filter'
                  ? <CalendarToolbarIcon viewOption={viewOption} />
                  : <BaseFilterMenu isDisplayLabel={false} items={filterItems} />}
              </ToggleButton>
            </BaseTooltip>
          ))}
        </Stack>
      )}

      <Box
        {...(isMobile && {
          justifyContent: 'space-between',
          pt: 2,
          pb: 2,
          pl: 2,
        })}
        display={isMobile ? 'flex' : 'contents'}
      >
        {isPrintMode
          ? <img src={appLogo} width={70} height={70} alt="appLogo" />
          : (
            <Typography
              variant="h5"
              sx={{
                my: { xs: 1, sm: 0 },
                color: isLight ? theme.palette.grey[900] : theme.palette.grey[100],
              }}
            >
              {t(`${month}`)}
              {' '}
              {moment(date).format('yyyy')}
            </Typography>
          )}

        {!isPrintMode && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={onPrevDate}>
            <Icon path={mdiChevronLeft} size={0.8} />
          </IconButton>

          <Button
            size="small"
            color="primary"
            onClick={onToday}
            sx={{ mx: 0.5 }}
          >
            {t('today')}
          </Button>

          <IconButton onClick={onNextDate}>
            <Icon path={mdiChevronRight} size={0.8} />
          </IconButton>
          {isDisplayPublishBth
            ? (
              <Box ml={2}>
                <LoadingButton loading={isLoading} onClick={publishSchedules} variant="contained">{t('publish')}</LoadingButton>
              </Box>
            )
            : null}

        </Box>
        )}
      </Box>
    </ToolbarWrapper>
  );
};

export {
  CalendarToolbar,
};
